import { ReactElement, useEffect, useState, useRef } from 'react';
import { Button } from '@/components/Button';
import { apiGetRequest, apiPatchRequest, apiPostRequest } from '@/utils/apiClient';
import { detectDeviceAndBrowser, getDeviceBrowserLabel } from '@/utils/deviceAndBrowser';
import { getAppType } from '@/utils/loginInfo';
import { useTranslation } from 'react-i18next';
import './FeedbackForm.scss';

const FEEDBACK_CODE = 'ANALYSIS_RESULT';

type FeedbackTemplateResponse = {
  feedbackCode: string;
  version: number;
};

type SubmitFeedbackResponse = {
  feedbackId: number;
};

const FeedbackForm = (): ReactElement => {
  const [step, setStep] = useState<'radio' | 'textarea' | 'thanks'>('radio');
  const [radioValue, setRadioValue] = useState<string>('');
  const [textareaValue, setTextareaValue] = useState<string>('');
  const [template, setTemplate] = useState<FeedbackTemplateResponse | null>(null);
  const [feedbackId, setFeedbackId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { t, i18n } = useTranslation('analysis');
  const questionLabel = t('feedback.question');
  const instructionLabel = t('feedback.instructions');
  const yesLabel = t('feedback.options.yes');
  const noLabel = t('feedback.options.no');
  const submitLabel = t('feedback.submit');
  const thanksLabel = t('feedback.thanks');
  const submitErrorLabel = t('feedback.submitError');

  useEffect(() => {
    const loadTemplate = async () => {
      setSubmitError(null);
      try {
        const localeParam = encodeURIComponent(i18n.language ?? '');
        const response = await apiGetRequest(
          `/api/feedback/templates/${FEEDBACK_CODE}?locale=${localeParam}`,
        );
        if (!response.ok) {
          throw new Error('failed to load feedback template');
        }
        setTemplate(await response.json());
      } catch {
        setSubmitError(submitErrorLabel);
      }
    };

    void loadTemplate();
  }, [i18n.language, submitErrorLabel]);

  // ラジオボタン送信
  const handleRadioChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRadioValue(value);
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      if (!template) {
        throw new Error('feedback template is not loaded');
      }
      const response = await apiPostRequest('/api/feedback/submit', {
        feedbackCode: template.feedbackCode ?? FEEDBACK_CODE,
        templateVersion: template.version,
        ratingValue: value === 'yes' ? 1 : 0,
        selectedOptions: [],
        comment: null,
        deviceBrowser: getDeviceBrowserLabel(detectDeviceAndBrowser()),
        appType: getAppType(),
        locale: i18n.language,
      });
      if (!response.ok) {
        throw new Error('feedback submit failed');
      }
      const data: SubmitFeedbackResponse = await response.json();
      setFeedbackId(data.feedbackId);
      setStep('textarea');
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    } catch {
      setRadioValue('');
      setSubmitError(submitErrorLabel);
    } finally {
      setIsSubmitting(false);
    }
  };

  // テキストエリア送信
  const handleTextareaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!textareaValue || feedbackId === null) return;

    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const response = await apiPatchRequest(`/api/feedback/${feedbackId}/comment`, {
        comment: textareaValue,
      });
      if (!response.ok) {
        throw new Error('feedback comment update failed');
      }
      setStep('thanks');
    } catch {
      setSubmitError(submitErrorLabel);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="feedbackform" className="w-full mt-0 text-center">
      <div className="min-h-60 lg:min-h-60 m-6 mt-0 p-6 pt-16 lg:pt-16 text-center border rounded-lg">
        {step === 'radio' && (
          <form className="lg:w-6/12 mx-auto">
            <p className="mb-6 text-lg font-semibold">
              <span className="feedback-question-attention">{questionLabel}</span>
            </p>
            <div className="flex justify-center gap-4 sm:gap-8">
              <label className="inline-flex min-w-24 cursor-pointer items-center justify-center gap-2 rounded-full border border-white bg-blue-600 px-5 py-3 text-base text-white transition hover:bg-blue-700 sm:min-w-36 sm:gap-3 sm:px-8 sm:py-3.5 sm:text-lg">
                <input
                  type="radio"
                  id="radio1"
                  name="analysis-feedback-rating"
                  value="yes"
                  className="hidden peer"
                  checked={radioValue === 'yes'}
                  onChange={handleRadioChange}
                  disabled={!template || isSubmitting}
                />
                <span className="text-xl" aria-hidden="true">👍</span>
                <span className="peer-checked:font-bold">{yesLabel}</span>
              </label>
              <label className="inline-flex min-w-24 cursor-pointer items-center justify-center gap-2 rounded-full border border-white bg-blue-600 px-5 py-3 text-base text-white transition hover:bg-blue-700 sm:min-w-36 sm:gap-3 sm:px-8 sm:py-3.5 sm:text-lg">
                <input
                  type="radio"
                  id="radio2"
                  name="analysis-feedback-rating"
                  value="no"
                  className="hidden peer"
                  checked={radioValue === 'no'}
                  onChange={handleRadioChange}
                  disabled={!template || isSubmitting}
                />
                <span className="text-xl" aria-hidden="true">😑</span>
                <span className="peer-checked:font-bold">{noLabel}</span>
              </label>
            </div>
          </form>
        )}
        {step === 'textarea' && (
          <form onSubmit={handleTextareaSubmit} className="lg:w-6/12 mx-auto">
            <p className="mb-3">{instructionLabel}</p>
            <textarea
              ref={textareaRef}
              name="analysis-feedback-comment"
              required
              maxLength={5000}
              className="form-control mb-5 w-full border rounded p-2"
              rows={3}
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
            />
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {submitLabel}
            </Button>
          </form>
        )}
        {step === 'thanks' && (
          <div className="text-lg font-semibold py-8">{thanksLabel}</div>
        )}
        {submitError && <p className="mt-4 text-sm text-red-600">{submitError}</p>}
      </div>
    </div>
  );
};

export default FeedbackForm;
